import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import { applyReward, getRewardState } from '@/api/widget/rewards'
import { EMAIL_REGEX, PHONE_REGEX } from '@/constants'
import { RewardState } from '@/types/widget'
import { logger, showErrorToast, showSuccessToast } from '@/utils'
import { track } from '@/utils/analytics'
import { getDeviceId } from '@/utils/device'
import { getContactID, getCurrentUID } from './useSessionStore'
import { useWidgetSignInFormStore } from './useWidgetSignInFormStore'
import { useFlowState } from './useFlowState'

type RewardsStoreState = {
  loading: boolean
  claiming: boolean
  claimed: boolean
  recReward: RewardState | undefined
  recSignupLink: string | undefined
  error: string | undefined
}

type RewardsStoreActions = {
  fetchRecReward: () => Promise<void>
  claimRecReward: () => Promise<void>
  reset: () => void
}

type RewardsStore = RewardsStoreState & RewardsStoreActions

const initialState: RewardsStoreState = {
  loading: false,
  claiming: false,
  claimed: false,
  recReward: undefined,
  recSignupLink: undefined,
  error: undefined,
}

export const useRewardsStore = create<RewardsStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      fetchRecReward: async () => {
        const uid = getCurrentUID()
        if (!uid) {
          logger.error('fetchRecReward', 'No current UID set')
          return
        }

        set({ loading: true, error: undefined }, false, 'fetchRecReward/start')

        const [data, error] = await getRewardState(uid, {
          rewardType: 'rec',
          deviceId: getDeviceId(),
          contactID: getContactID(),
        })

        if (error) {
          logger.error('fetchRecReward', error)
          set({ loading: false, error }, false, 'fetchRecReward/error')
          return
        }

        set(
          { loading: false, recReward: data?.rec, recSignupLink: data?.signupLink ?? undefined },
          false,
          'fetchRecReward/success'
        )
      },

      claimRecReward: async () => {
        if (get().claiming || get().claimed) return

        const uid = getCurrentUID()
        if (!uid) {
          showErrorToast('Missing session context')
          return
        }

        const { phone = '', email = '' } = useWidgetSignInFormStore.getState()
        const isPhoneValid = PHONE_REGEX.test(phone)
        const isEmailValid = EMAIL_REGEX.test(email)

        if (!isPhoneValid && !isEmailValid) {
          showErrorToast('Please enter a valid phone number or email address')
          return
        }

        set({ claiming: true, error: undefined }, false, 'claimRecReward/start')

        const [, error] = await applyReward(uid, {
          rewardType: 'rec',
          phone: isPhoneValid ? phone : undefined,
          email: isEmailValid ? email : undefined,
        })

        if (error) {
          logger.error('claimRecReward', error)
          showErrorToast(error)
          set({ claiming: false, error }, false, 'claimRecReward/error')
          return
        }

        set({ claiming: false, claimed: true }, false, 'claimRecReward/success')
        showSuccessToast('Points claimed!')
        track('reward', 'claim_rec_reward', { rewardType: 'rec' })
        useFlowState.getState().goForward()
      },

      reset: () => set(initialState, false, 'reset'),
    }),
    { name: 'RewardsStore', enabled: process.env.NODE_ENV === 'development' }
  )
)
